import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { useHospitalInfo } from "@/hooks/useHospitalInfo";
import { SquareArrowOutUpRight } from "lucide-react";

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const { hospitalInfo } = useHospitalInfo();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-white/10 bg-black/35 backdrop-blur-xl py-6 w-full">
      <div className="w-full mx-auto px-4 md:px-6 lg:px-8">
        {/* Top Section - Apple Style */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
          <div className="space-y-3 md:col-span-1">
            {/* Logo & Site Name */}
            <div className="flex items-center">
              <img
                src="/images/logo/fullLogo-white.png"
                alt="Logo"
                className="max-h-56 w-auto mr-2"
              />
            </div>
          </div>

          <div className="space-y-3 md:col-span-1">
            <h3 className="text-white/90 text-xs font-semibold tracking-wide uppercase mb-2">
              {t("siteDepartment")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("home")}
                </Link>
              </li>
              <li>
                <Link
                  to="/"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("dashboard")}
                </Link>
              </li>
              <li>
                <Link
                  to="/user/courses"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("allCourses")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-3 md:col-span-1">
            <h3 className="text-white/90 text-xs font-semibold tracking-wide uppercase mb-2">
              <SquareArrowOutUpRight className="inline-block w-3 h-3 m-1" />
              {t("importantLinks")}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="https://www.moh.gov.sa/Pages/default.aspx"
                  target="_blank"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("ministryOfHealth")}
                </Link>
              </li>
              <li>
                <a
                  href="http://health.sa/ar"
                  target="_blank"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("healthCompany")}
                </a>
              </li>
              <li>
                <a
                  href="https://www.moh.gov.sa/Ministry/MediaCenter/Publications/Documents/HAQOQ%20BOOK%20OK.pdf"
                  target="_blank"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("employeesRightsGuide")}
                </a>
              </li>
              <li>
                <a
                  href="http://volunteer.srca.org.sa/#!/ar/home"
                  target="_blank"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("volunteerApp")}
                </a>
              </li>
            </ul>
          </div>

          {/* <div className="space-y-3 md:col-span-1">
            <h3 className="text-white/90 text-xs font-semibold tracking-wide uppercase mb-2">
              {t("resources")}
            </h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="#"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("documentation")}
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("tutorials")}
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("support")}
                </a>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {t("privacyPolicy")}
                </Link>
              </li>
            </ul>
          </div> */}

          <div className="space-y-3 md:col-span-1">
            <h3 className="text-white/90 text-xs font-semibold tracking-wide uppercase mb-2">
              {t("contact")}
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-white/60 ml-0 mr-2 rtl:mr-0 rtl:ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href={`mailto:${hospitalInfo?.email || t("contactEmail")}`}
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {hospitalInfo?.email || t("contactEmail")}
                </a>
              </li>
              <li className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-white/60 ml-0 mr-2 rtl:mr-0 rtl:ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <a
                  href={`tel:${hospitalInfo?.phone || t("contactPhone")}`}
                  className="text-white/60 text-xs hover:text-white transition-all"
                >
                  {hospitalInfo?.phone || t("contactPhone")}
                </a>
              </li>
              <li className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-white/60 ml-0 mr-2 rtl:mr-0 rtl:ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-white/60 text-xs">
                  {hospitalInfo?.address ||
                    t("address") ||
                    "جدة، المملكة العربية السعودية"}
                </span>
              </li>
              {hospitalInfo?.website && (
                <li className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 text-white/60 ml-0 mr-2 rtl:mr-0 rtl:ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                  <a
                    href={hospitalInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 text-xs hover:text-white transition-all"
                  >
                    {hospitalInfo.website}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Middle Section - Newsletter - Apple Style */}
        <div className="border-t border-b border-white/10 py-8 my-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between"></div>
        </div>

        {/* Bottom section - Legal and Social - Apple Style */}
        <div className="flex flex-col-reverse md:flex-row justify-between items-center pt-2">
          <div className="flex flex-wrap items-center justify-center md:justify-start mt-4 md:mt-0">
            <p className="text-white/40 text-[11px] mb-2 md:mb-0 text-center md:text-left">
              &copy; {currentYear} {t("siteDepartment")}.
              {t("copyright") || "جميع الحقوق محفوظة."}
            </p>
            <span className="hidden md:inline text-white/40 mx-2">|</span>
            <a
              href="/terms"
              className="text-white/40 text-[11px] hover:text-white/60 mx-2 transition-colors"
            >
              {t("terms") || "شروط الاستخدام"}
            </a>
            <a
              href="#"
              className="text-white/40 text-[11px] hover:text-white/60 mx-2 transition-colors"
            >
              {t("privacyPolicy")}
            </a>
            <a
              href="/privacy-policy"
              className="text-white/40 text-[11px] hover:text-white/60 mx-2 transition-colors"
            >
              {t("cookies")}
            </a>
            <a
              href="#"
              className="text-white/40 text-[11px] hover:text-white/60 mx-2 transition-colors"
            >
              {t("sitemap") || "خريطة الموقع"}
            </a>
          </div>

          <div className="flex items-center space-x-6 rtl:space-x-reverse">
            {hospitalInfo?.social_links?.twitter && (
              <a
                href={hospitalInfo.social_links.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </a>
            )}
            {hospitalInfo?.social_links?.instagram && (
              <a
                href={hospitalInfo.social_links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            )}
            {hospitalInfo?.social_links?.facebook && (
              <a
                href={hospitalInfo.social_links.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                </svg>
              </a>
            )}
            {hospitalInfo?.social_links?.linkedin && (
              <a
                href={hospitalInfo.social_links.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                </svg>
              </a>
            )}

            {/* Show default social links if no hospital social links are available */}
            {!hospitalInfo?.social_links && (
              <>
                <a
                  href="#"
                  className="text-white/50 hover:text-white transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="text-white/50 hover:text-white transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="text-white/50 hover:text-white transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="text-white/50 hover:text-white transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
